import { isStructuredLayout } from "@/shared/utils/is-structured-layout";

/**
 * The narrowest shape this predicate needs: `StoryType`, `IPage` and the
 * services' `TPage` all satisfy it, and none of them is assignable to the
 * others, so the call sites would otherwise need casts.
 */
type PageLike = {
  layoutVersion?: string | null;
  settings?: {
    canonical?: string | null;
    contentPointer?: string | null;
    publicContentPointer?: string | null;
    [key: string]: unknown;
  } | null;
} | null;

/**
 * Does the story hold saved content that was never published?
 *
 * `settings.contentPointer` is the collaboration head — every autosave
 * (`record-revision`) advances it. `settings.publicContentPointer` is the
 * pointer that was live at the last publish: `usePublish` copies
 * `contentPointer` into it before `save-and-publish`, and the backend's
 * `publishCollaborative` re-copies it from the page's own fresh pointer. So the
 * two are equal immediately after a publish and diverge on the first edit
 * afterwards — which makes the comparison an exact test for "the published
 * artifacts (the story template included) are behind the editor", with none of
 * the second-resolution fuzziness of `isPageChanged`'s updatedAt/publishedAt.
 *
 * Legacy (non-structured) layouts keep their slides on the page document rather
 * than behind a pointer, so they are never reported as diverged.
 */
export const hasUnpublishedContent = (page: PageLike): boolean => {
  const settings = page?.settings;
  if (!settings) return false;
  if (!isStructuredLayout(page?.layoutVersion || "")) return false;

  const { canonical, contentPointer, publicContentPointer } = settings;
  if (typeof canonical !== "string" || !canonical) return false;
  if (typeof contentPointer !== "string" || !contentPointer) return false;

  return contentPointer !== publicContentPointer;
};
